import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { chatWidgetTroubleshootingLastResultSelector } from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { globalJavascriptBaseUrlSelector } from '@mm/state/selectors/envSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { Result } from '../ChatWidgetTroubleshootingToolResult';
import {
  PrimaryButton,
  ButtonColor,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';

type Props = {
  isXRay?: boolean;
};

export const useJSLoadedResult = (props: Props): Result => {
  const { isXRay } = props;
  const [expanded, setExpanded] = useState(false);
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const globalJavascriptBaseUrl = useSelector(globalJavascriptBaseUrlSelector);

  if (!result || !facebookPage) {
    return;
  }

  const infoTooltip = (
    <div className="chat-widget-troubleshooting-tool-result__more-info">
      The following script
      {result.globalJS.urlsLoaded.length > 1 ? 's were' : ' was'} loaded:{' '}
      <ul>
        {result.globalJS.urlsLoaded.map(url => (
          <li key={url}>
            <code>{url}</code>
          </li>
        ))}
      </ul>
      {result.globalJS.correctPageId === false && (
        <>
          Please install this script instead:
          <ul>
            <li>
              <code>
                https://{globalJavascriptBaseUrl}/js/{facebookPage.remoteId}.js
              </code>
            </li>
          </ul>
        </>
      )}
    </div>
  );

  if (!result.globalJS.loaded) {
    return {
      result: 'error',
      description: isXRay
        ? 'Email detector script not detected'
        : 'Chat widget script not detected',
      infoTooltip: (
        <>
          Please install the {isXRay ? 'email detector' : 'chat widget'} script
          inside the <code>&lt;head&gt;</code> tag on every page on your website
        </>
      ),
    };
  }
  if (result.globalJS.correctPageId === false) {
    return {
      result: 'error',
      description: (
        <>
          Incorrect {isXRay ? 'email detector' : 'chat widget'} script detected
          <PrimaryButton
            color={ButtonColor.transparent}
            className="chat-widget-troubleshooting-tool-result__toggle-button"
            onClick={() => setExpanded(s => !s)}
          >
            {expanded ? 'Hide' : 'Show'} Details
          </PrimaryButton>
          {expanded && infoTooltip}
        </>
      ),
    };
  }
  if (result.globalJS.loadedMoreThanOnce) {
    return {
      result: 'error',
      description: (
        <>
          {isXRay ? 'Email detector script' : 'Chat widget script'} loaded
          multiple times
          <PrimaryButton
            color={ButtonColor.transparent}
            className="chat-widget-troubleshooting-tool-result__toggle-button"
            onClick={() => setExpanded(s => !s)}
          >
            {expanded ? 'Hide' : 'Show'} Details
          </PrimaryButton>
          {expanded && infoTooltip}
        </>
      ),
    };
  }
  return {
    result: 'ok',
    description: isXRay
      ? 'Email detector script detected'
      : 'Chat widget script detected',
  };
};
